<template>
  <div v-if="widget" class="my-space-card my-space-card--scrollable" :class="{ 'my-space-card--mobile-collapsed': collapsed[widget.key] && !expanded, 'my-space-card--expanded': expanded }" :role="expanded ? 'dialog' : null" :aria-modal="expanded ? 'true' : null" tabindex="-1" @keydown.esc="expanded = false">
      <div v-if="expanded" class="my-space-card-modal-backdrop" aria-hidden="true" @click="expanded = false"></div>
      <div class="my-space-card-header">
        <span class="my-space-card-title"><span class="my-space-card-icon"><AppIcon :name="widget.icon" /></span>{{ widget.title }}</span>
        <button type="button" class="my-space-card-refresh" :disabled="loading || refreshing" v-b-tooltip.hover :title="'Actualizar ' + widget.title.toLowerCase()" @click.stop="refresh">
          <AppIcon name="refresh" :class="{ 'my-space-card-refresh__icon--spinning': refreshing }" />
        </button>
        <button type="button" class="my-space-card-expand" :aria-expanded="String(expanded)" :title="expanded ? 'Cerrar' : 'Abrir vista ampliada'" @click.stop="expanded = !expanded"><AppIcon :name="expanded ? 'close' : 'open_in_full'" /></button>
        <button v-if="!expanded" type="button" class="my-space-card-collapse" :aria-expanded="String(!collapsed[widget.key])" @click="toggle(widget.key)"><AppIcon :name="collapsed[widget.key] ? 'expand_more' : 'expand_less'" /></button>
      </div>
      <div class="my-space-card-body">
        <div v-if="loading" class="my-space-card-loading"><b-spinner small /></div>
        <template v-else>
          <div class="my-space-task-tree__children my-space-personal-tasks__list">
            <div v-for="item in widget.items" :key="item.id" class="my-space-task-tree__task my-space-personal-tasks__task" @click="goTasks">
              <AppIcon name="check_box_outline_blank" class="my-space-task-tree__task-icon" />
              <span class="my-space-day-item-text">{{ item.title }}</span>
              <AppIcon v-if="item.reminder" name="notifications" class="my-space-task-reminder-icon" v-b-tooltip.hover :title="reminderLabel(item.reminder)" />
              <TaskChecklistProgress :progress="item.checklist_progress" :status="item.checklist_status" />
              <span v-if="item.category" class="my-space-personal-tasks__category" :title="item.category">{{ item.category }}</span>
              <span class="my-space-personal-tasks__priority" :class="'my-space-personal-tasks__priority--' + item.priority">{{ priorityLabel(item.priority) }}</span>
              <button type="button" class="my-space-item-complete" :disabled="refreshing || completingId === item.id" @click.stop="complete(item)"><AppIcon name="check" /></button>
            </div>
          </div>
          <EmptyState v-if="!widget.items.length" :icon="widget.icon" :title="widget.emptyTitle" :description="widget.emptyDescription" />
        </template>
      </div>
      <div class="my-space-card-footer" @click="goTasks"><span>Ver todas</span><AppIcon name="chevron-right" font-scale="0.8" class="my-space-card-footer-arrow" /></div>
  </div>
</template>

<script>
import serviceTasks from '../staff/services'
import ErrorToken from '@/handler/errorToken'
import Error from '@/handler/error'
import { EventBus } from '@/handler/event-bus'
import TaskChecklistProgress from './TaskChecklistProgress'
import moment from 'moment'

export default {
  name: 'MySpaceTasksWidgets',
  components: { TaskChecklistProgress },
  props: {
    type: { type: String, required: true },
  },
  data: () => ({ loading: true, refreshing: false, completingId: null, personal: [], collapsed: { personalTasks: window.innerWidth <= 767 }, expanded: false }),
  computed: {
    widget() {
      var widgets = [
        { key: 'personalTasks', title: 'Mis tareas personales', icon: 'person', items: this.orderedPersonalTasks, emptyTitle: 'Estás al día', emptyDescription: 'No tenés tareas personales pendientes.' },
      ]
      return widgets.find(widget => widget.key === this.type)
    },
    orderedPersonalTasks() {
      var priorityRank = { urgent: 1, high: 2, medium: 3, low: 4 }
      return this.personal.slice().sort((first, second) => {
        var priorityDifference = (priorityRank[first.priority] || 5) - (priorityRank[second.priority] || 5)
        if (priorityDifference) return priorityDifference

        var sortDifference = Number(first.sort_order || 0) - Number(second.sort_order || 0)
        if (sortDifference) return sortDifference

        return new Date(second.created_at).getTime() - new Date(first.created_at).getTime()
      })
    },
  },
  mounted() { EventBus.$on('MySpace:tasks-refresh', this.refresh); this.load() },
  beforeDestroy() { EventBus.$off('MySpace:tasks-refresh', this.refresh) },
  methods: {
    load(refresh = false) { if (refresh) this.refreshing = true; serviceTasks.obtenerTareas({ status: 'pending', relation_type: 'personal' }).then(response => { const items = Array.isArray(response.data) ? response.data : []; this.personal = items; this.loading = false; this.refreshing = false }).catch(error => { this.loading = false; this.refreshing = false; this.$awn.alert(ErrorToken.valid(error)) }) },
    refresh() { if (!this.loading && !this.refreshing) this.load(true) },
    complete(item) {
      if (!item || this.completingId || this.refreshing) return

      var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: 'check2-circle' } }),
        'Completar tarea',
      ])
      this.$bvModal.msgBoxConfirm('¿Completar la tarea "' + item.title + '"?', {
        title: title,
        size: 'sm',
        buttonSize: 'sm',
        modalClass: 'my-space-confirm-modal',
        dialogClass: 'my-space-completion-confirm-dialog',
        headerClass: 'app-confirm-header my-space-confirm-header--success',
        footerClass: 'p-2',
        hideHeaderClose: false,
        okVariant: 'success',
        okTitle: 'COMPLETAR',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(confirmed => {
        if (!confirmed) return

        this.completingId = item.id
        serviceTasks.completarTarea(item.id).then(() => {
          this.completingId = null
          this.$awn.success('Tarea completada')
          this.load(true)
        }).catch(error => {
          this.completingId = null
          const message = Error.showError(error)
          if (String(message).toLowerCase().indexOf('checklist') !== -1) this.$awn.warning(message)
          else this.$awn.alert(message)
        })
      })
    },
    toggle(key) { this.collapsed[key] = !this.collapsed[key] },
    priorityLabel(priority) { return { urgent: 'Urgente', high: 'Alta', medium: 'Media', low: 'Baja' }[priority] || 'Sin prioridad' },
    reminderLabel(reminder) { return 'Recordatorio: ' + moment(reminder.remind_at).format('DD/MM/YYYY HH:mm') },
    goTasks() { this.$router.push({ name: 'TasksStaffCrud', query: { context: 'mine', relation_type: 'personal', my_space_filter: String(Date.now()) } }).catch(() => {}) },
  },
}
</script>

<style scoped>
.my-space-personal-tasks__task { padding-left: 10px; }
.my-space-personal-tasks__priority { margin-left: 8px; font-size: var(--app-text-xs); font-weight: var(--app-font-bold); color: var(--app-color-text-muted); }
.my-space-personal-tasks__priority--urgent { color: var(--app-color-danger); }
.my-space-personal-tasks__priority--high { color: var(--app-color-warning); }
.my-space-personal-tasks__priority--medium { color: var(--app-color-primary); }
.my-space-personal-tasks__category { flex: 0 1 auto; max-width: 82px; overflow: hidden; color: var(--app-color-text-muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.my-space-task-reminder-icon { flex: 0 0 auto; color: var(--app-color-primary); font-size: 16px; }
</style>
